import { useEffect, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { InlineAlert, LoadingSpinner, SearchableSelect, UniversalPageLayout } from "../../components/BaseComponents";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import routes from "../../components/Router/routes";
import useApi from "../../hooks/useApi";
import useCurrencyFormatter from "../../hooks/useCurrencyFormatter";
import useDataSort from "../../hooks/useDataSort";
import usePagination from "../../hooks/usePagination";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { centsToCurrency, getApiErrorMessage } from "../../lib/utils";
import { useProjectDetailZones } from "./useProjectDetailZones";
import CompanyProjectEditModal from "./CompanyProjectEditModal";
import NumericText from "../../components/BaseComponents/NumericText/NumericText";

function CompanyProject() {
    const { projectId } = useParams();

	const formatCurrency = useCurrencyFormatter();
    const [listData, setListData ] = useState<any[]>([])
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    // Issue #884: loadSummary/loadListdata/the entries `run()` previously had no catch at all, so
    // a rejecting GET (including the now-rejecting ACCESS_AUDIT_UNAVAILABLE notify-only code -
    // #857 item 6) left `loadingSummary`/`loading` stuck true (the summary gate below hid the
    // whole page behind a spinner forever) and produced an unhandled rejection, with no visible
    // feedback. These mirror CompanyProjectsList's existing summaryError pattern instead.
    const [summaryError, setSummaryError] = useState<any>();
    const [entriesError, setEntriesError] = useState<any>();

    const [summary, setSummary] = useState<any>({});

    const [grouping, setGrouping] = useState<'purchases'|'suppliers'>('suppliers');

    const [scope, setScope] = useState('');

	const [editCandidate, setEditCandidate] = useState<any>(null);
    const dataSort = useDataSort('supplierName', 'desc');
    const pagination = usePagination(1, 20);

    // Debounce searchText so typing doesn't fire an API request on every keystroke
    const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchText, () => setDebouncedSearchText(searchText))

    const api = useApi();

    // Extracted (rather than left inline in the mount effect) so onEdited's post-save refresh
    // below can reuse the exact same error handling instead of duplicating an uncaught copy.
    const loadListData = async () => {
        try {
            const result = await api.get('/projects?sortKey=name&sortDirection=asc');
            setListData(result.data.rows);
        } catch {
            // Non-blocking: this only populates the project-switcher dropdown, so a failure here
            // must not hide the rest of the already-loaded page - just leave the dropdown as-is.
            console.warn('Failed to load the project list.');
        }
    }

    const loadSummary = async () => {
        setLoadingSummary(true);
        setSummaryError(undefined);
        try {
            const result = await api.get(`/projects/${projectId}`);
            setSummary(result.projectSummary);
        } catch (err: any) {
            setSummaryError(err);
        } finally {
            setLoadingSummary(false);
        }
    }

    useEffect(() => {
        loadListData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    const columns: TableColumn[] = grouping === 'purchases'
        ? [
            {
                id:'itemName',
                label: 'Item Name',
                sortable: true,
            },
            {
                id:'level1',
                label: 'Emission Type',
                sortable: true,
            },
            {
                id:'totalSpend',
                label: 'Total Spend',
                sortable: true,
                renderer: (key, data) => <span><NumericText value={formatCurrency(Number(centsToCurrency(data[key])))} /></span>,
                textAlign: 'right',
            },
            {
                id:'totalEmissions',
                label: 'CO₂e Emissions',
                sortable: true,
                renderer: (key, data) => <span><NumericText value={data[key]} /> kgCO₂e</span>,
                textAlign: 'right',
            }
        ]
        : [
            {
                id:'supplierName',
                label: 'Supplier Name',
                sortable: true,
            },
            {
                id:'totalSpend',
                label: 'Total Spend',
                sortable: true,
                renderer: (key, data) => <span><NumericText value={formatCurrency(Number(centsToCurrency(data[key])))} /></span>,
                textAlign: 'right',
            },
            {
                id:'totalEmissions',
                label: 'CO₂e Emissions',
                sortable: true,
                renderer: (key, data) => <span><NumericText value={data[key]} /> kgCO₂e</span>,
                textAlign: 'right',
            },
            {
                id:'invite',
                label: '',
            }
        ]

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setEntriesError(undefined);
        const run = async () => {
            const qs = new URLSearchParams({
                grouping,
                searchText: debouncedSearchText,
                ...dataSort.params,
                ...pagination.params,
                scope,
            })
            try {
                const results = await api.get(`/projects/${projectId}/entries?` + qs)
                if(cancelled) {
                    return;
                }
                setData(results.data.rows);
                pagination.readMeta(results.data.meta);
            } catch (err: any) {
                if(cancelled) {
                    return;
                }
                setEntriesError(getApiErrorMessage(err, 'Failed to load this project\'s data. Please try again.'));
            } finally {
                if(!cancelled) {
                    setLoading(false);
                }
            }
        }
        run();
        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params, grouping, projectId, dataSort.params, scope, debouncedSearchText])

    const navigate = useNavigate();

    const projectSelector = (
        <SearchableSelect
            options={listData.map((project) => ({ value: String(project.id), label: project.name }))}
            isSearchable={false}
            isClearable={false}
            onChange={(option) => {
                if (option) {
                    navigate(generatePath(routes.companyProjects.path, { projectId: option.value }))
                }
            }}
            value={listData
                .map((project) => ({ value: String(project.id), label: project.name }))
                .filter((option) => option.value === String(projectId ?? ''))}
        />
    )

    const tableWithPagination = (
        <>
            { entriesError && <InlineAlert type='error' className='mb-4'>{ entriesError }</InlineAlert> }
            <SimpleSortTable
                columns={columns}
                data={data}
                isLoading={loading}
                defaultSorting={dataSort.defaultSorting}
                onSortPress={dataSort.onChange}
            />
            <Pagination
                pagination={pagination.pagination}
                onPageChange={pagination.onPageChange}
                isLoading={loading}
            />
        </>
    )

    // Find current project from listData (has name, code, totalTurnoverAmount) for edit modal
    const currentProject = listData.find(p => p.id === projectId)

    const zones = useProjectDetailZones({
        projectSelector,
        summary,
        summaryError,
        scope,
        onScopeChange: (id) => setScope(id),
        grouping,
        onGroupingChange: (newGrouping) => {
            setGrouping(newGrouping)
            dataSort.onChange(newGrouping === 'suppliers' ? 'supplierName' : 'itemName', 'ascending')
        },
        searchText,
        onSearchChange: (event) => setSearchText(event.target.value),
        onSearchKeyDown: (event) => {
            if (event.key === 'Enter') {
                flushSearch()
            }
        },
        onSearchClear: () => {
            setSearchText('')
            flushSearch()
        },
        searchInProgress,
        tableComponent: tableWithPagination,
        isLoading: loading,
        onEditClick: () => setEditCandidate(currentProject),
    })

    // Issue #884: previously gated on `loadingSummary` alone, so a failed loadSummary() (which
    // used to never resolve for ACCESS_AUDIT_UNAVAILABLE pre-#857, and simply had no catch after
    // it) left this spinner showing forever with nothing else on the page reachable. loadSummary
    // now always clears `loadingSummary` (success or failure, see above) and surfaces a failure
    // via summaryError on the summary panel below instead, matching CompanyProjectsList.
    if(loadingSummary) {
        return <LoadingSpinner />
    }

    return (
        <>
            <UniversalPageLayout
                documentTitle={routes.companyProjects.name}
                heading={{
                    crumbs: [
                        { label: 'Purchases', route: routes.companyPurchases.path },
                        { label: routes.companyProjects.menuName as string, route: generatePath(routes.companyProjects.path, { projectId: undefined }) },
                        { label: 'Views' },
                    ],
                }}
                primarySelector={zones.primarySelector}
                viewSelector={zones.viewSelector}
                summary={zones.summary}
                tabs={zones.tabs}
                filtersSimple={zones.filtersSimple}
                actions={zones.actions}
            >
                {zones.table}
            </UniversalPageLayout>
            {editCandidate && (
                <CompanyProjectEditModal
                    open={!!editCandidate}
                    onClose={() => setEditCandidate(null)}
                    projectId={editCandidate.id}
                    projectName={editCandidate.name}
                    projectCode={editCandidate.code}
                    projectValue={editCandidate.totalTurnoverAmount}
                    onEdited={() => {
                        setEditCandidate(null)
                        // Reload project list and summary to get updated data - same error
                        // handling as the mount effects above, not a duplicated uncaught copy.
                        loadListData();
                        loadSummary();
                    }}
                />
            )}
        </>
    )
}

export default CompanyProject;
